<template>
    <div class="flex-1">
        <skeleton v-if="plan === undefined" />
        <not-found v-else-if="plan === null" />
        <purchase-gift-card
            v-else
            :plan="plan"
            :is-editing="false"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onServerPrefetch } from 'vue'
import PurchaseGiftCard from '~/components/PurchaseContract/GiftCard/PurchaseGiftCard.vue'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import Skeleton from 'primevue/skeleton'
import NotFound from '~/components/NotFound/NotFound.vue'

import { useSelectedLanguage } from '~/composables/use-selected-language'
const props = defineProps<{ planId: string }>()
const planId = computed(() => props.planId)
const activeFacility = useActiveFacility()

const query = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const { data, suspense } = useQuery({
    queryKey: [
        'GiftCardPurchaseQuery',
        planId,
        activeFacility,
        selectedLanguage
    ],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query GiftCardPurchaseQuery(
                    $planId: ID!
                    $facilityId: ID!
                    $input: SimpleSpokeInput
                    $language: Language!
                ) {
                    plan(id: $planId) {
                        ...PurchaseGiftCardPlanFragment
                    }
                }
            `),
            {
                planId: planId.value,
                facilityId: activeFacility.value.id,
                input: {
                    facilityId: activeFacility.value.id
                },
                language: selectedLanguage.value
            }
        )
})

const plan = computed(() => {
    if (data.value === undefined) {
        return undefined
    }

    if (data.value.plan === null) {
        return null
    }

    return data.value.plan
})

onServerPrefetch(async () => {
    // Storefront node URLs need the plan ready during SSR so the purchase
    // view renders in the HTML.
    await suspense()
})
</script>
